import { MARKET_STATUSES, MARKET_TYPES } from '../constants/defaultValues';

export const checkMarketType = ({ marketType, specifiers }) => {
    if (!marketType) return true;

    let isValid = true;

    const specifierValues = specifiers.filter(specifier => !isNaN(specifier.value)).map(specifier => specifier.value);

    switch (marketType) {
        case MARKET_TYPES.integer: {
            if (specifierValues.some(value => value % 1 !== 0)) isValid = false;
            break;
        }
        case MARKET_TYPES.float: {
            if (specifierValues.some(value => value % 1 !== 0.5)) isValid = false;
            break;
        }
        case MARKET_TYPES.asian: {
            if (specifierValues.some(value => value % 1 !== 0.25 && value % 1 !== 0.75)) isValid = false;
            break;
        }
        default: {
            //
        }
    }

    return isValid;
};

export const groupByTemplateName = templates => {
    return templates.reduce((result, template) => {
        const { name } = template;

        if (!result[name]) {
            result[name] = [];
        }

        return {
            ...result,
            [name]: [...result[name], template],
        };
    }, {});
};

export const mergeDuplicateTemplateResultTypes = templates => {
    return Object.values(groupByTemplateName(templates)).map(template => {
        if (template.length > 1) {
            const resultTypes = template
                .reduce((result, template) => [...result, ...template.resultTypes], [])
                .reduce(
                    (result, resultType) => ({
                        ...result,
                        [resultType.resultTypeId]: resultType,
                    }),
                    {}
                );

            return {
                ...template[0],
                resultTypes: Object.values(resultTypes),
            };
        } else {
            return template[0];
        }
    });
};

export const filterTemplatesByMarketStatus = ({ templates, markets }) => {
    return templates.filter(template => {
        return (
            template.resultTypes.filter(resultType => {
                const foundMarkets = markets[resultType.marketRefId];

                let isMarketsMatched = false;

                if (foundMarkets) {
                    isMarketsMatched = foundMarkets.some(market => isMarketStatusMatched(market));
                }

                return foundMarkets && isMarketsMatched;
            }).length > 0
        );
    });
};

export const filterTemplatesByMarketType = ({ templates, markets }) => {
    return templates.filter(template => {
        return (
            template.resultTypes.filter(resultType => {
                const foundMarkets = markets[resultType.marketRefId];
                const { marketType } = template;

                let isMarketTypeMatched = true;

                if (foundMarkets && marketType) {
                    isMarketTypeMatched = foundMarkets.some(market =>
                        checkMarketType({
                            marketType,
                            specifiers: market.specifiers,
                        })
                    );
                }

                return foundMarkets && isMarketTypeMatched;
            }).length > 0
        );
    });
};

export const filterMarketsByMarketStatus = markets => {
    if (!markets) return [];

    return markets.filter(market => isMarketStatusMatched(market));
};

export const filterMarketsByMarketType = ({ markets, marketType }) => {
    if (!markets) return [];

    return markets.filter(market =>
        checkMarketType({
            marketType,
            specifiers: market.specifiers,
        })
    );
};

export const filterMarketsWithoutName = markets => {
    if (!markets) return [];

    return markets.filter(market => market.names && Object.keys(market.names).length > 0);
};

export const filterMarketsWithoutOutcomes = markets => {
    if (!markets) return [];

    return markets.filter(market => market.outcomes && market.outcomes.length > 0);
};

export const filterVisibleMarkets = ({ markets, marketType }) => {
    return filterMarketsByMarketType({
        markets: filterMarketsByMarketStatus(filterMarketsWithoutOutcomes(filterMarketsWithoutName(markets))),
        marketType,
    });
};

export const generateTemplateColumns = ({ templates, columnCount }) => {
    let templateColumns = {};

    for (let index = 0; index < columnCount; index++) {
        templateColumns[index] = {
            id: index,
            templates: [],
        };
    }

    templates.forEach((template, index) => {
        const column = index % columnCount;

        templateColumns[column].templates.push(template);
    });

    return templateColumns;
};

export const findUnConfiguredMarkets = ({ templates, markets }) => {
    const configuredMarketRefIds = new Set();

    templates.forEach(template => {
        template.resultTypes.forEach(resultType => {
            configuredMarketRefIds.add(resultType.marketRefId);
        });
    });

    const unConfiguredMarkets = [];

    Object.keys(markets).forEach(marketRefId => {
        if (!configuredMarketRefIds.has(marketRefId)) {
            const matchedMarkets = markets[marketRefId].filter(market => isMarketStatusMatched(market));
            unConfiguredMarkets.push(...matchedMarkets);
        }
    });

    return unConfiguredMarkets;
};

export const isMarketStatusMatched = market => {
    if (!market) return false;

    return !market.frozen && market.status !== MARKET_STATUSES.deactivated;
};
